import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { SidebarHeader } from '@features/sidebar/components/sidebar-header/SidebarHeader';
import {
  SIDEBAR_LABELS,
  SCREENS,
} from '@features/sidebar/constants';
import { AssistantMessage } from '@features/sidebar/components/assistant-message/AssistantMessage';
import { UserMessage } from '@features/sidebar/components/user-message/UserMessage';
import '@features/sidebar/Sidebar.scss';
import { ToggleButton } from '@features/sidebar/components/toggle-button/ToggleButton';
import { SidebarInput } from '@features/sidebar/components/sidebar-input/SidebarInput';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import { useAI } from 'src/hooks/useAI';

import { AIService } from 'src/services/ai';
import { useAuthStore } from 'src/store/auth';
import { AnimatedDots } from '@components/shared/animated-dots/AnimatedDots';
import { cn } from '@utils/index';
import { useInsuranceStore } from 'src/store/insurance';
import { transformApplicationData } from '@features/onboarding/utils/transformApplicationData';
import { encodeJSONToBase64 } from '@utils/base64';
import { useApplicationStore } from 'src/store/application';
import { applicationService } from 'src/services/application';
import { useGeneralStore } from 'src/store';
import { AssistantTyping } from '@features/sidebar/components/assistant-message/AssistantTyping';

export const Sidebar = ({
  wrapperPopupClassName,
  wrapperSidebarClassName,
}: {
  wrapperPopupClassName?: string;
  wrapperSidebarClassName?: string;
}) => {
  const location = useLocation();
  
  // Don't show sidebar on /thanks page
  if (location.pathname === '/thanks') {
    return null;
  }

  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [pendingMessageId, setPendingMessageId] = useState<string | null>(null);
  const [pendingMessageTimestamp, setPendingMessageTimestamp] = useState<number | null>(null);
  const { currentStep, data } = useOnboardingStore();
  const { chatHistory, chatLoading } = useAI();
  const { user } = useAuthStore();
  const plans = useInsuranceStore((state) => state.plans);
  const { hasReachedPhq } = useApplicationStore();
  const [labels, setLabels] = useState<typeof SIDEBAR_LABELS>(SIDEBAR_LABELS);
  const [sidebarIntroShown, setSidebarIntroShown] = useState<boolean>(() => {
    return localStorage.getItem('sidebarIntroShown') === '1';
  });
  const hasInitialized = useRef(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const mobileMessagesContainerRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mobileMessagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const initialIsMobile = window.innerWidth < SCREENS.xl;
    setIsMobile(initialIsMobile);
    // Set initial open state: closed on mobile, open on desktop
    setIsOpen(!initialIsMobile);
    
    function handleResize() {
      const currentIsMobile = window.innerWidth < SCREENS.xl;
      setIsMobile(currentIsMobile);
      // On desktop, always keep sidebar open
      // On mobile/tablet, only close if switching from desktop (don't close if user opened it manually)
      if (!currentIsMobile) {
        setIsOpen(true);
      }
      // Don't automatically close on mobile/tablet - let user control it via toggle button
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch dynamic labels from AI initialize (only once when user.firstName is available)
  useEffect(() => {
    if (!user?.firstName || hasInitialized.current || hasReachedPhq === null || !plans.length) {
      return;
    }

    (async () => {
      try {
        hasInitialized.current = true;
        setIsLoading(true);

        let progress = 0;
        let abandonedAt: string | null = null;
        let updatedAt: string | null = null;
        try {
          const currentApp = await applicationService.getApplications();
          progress = currentApp.progress ?? 0;
          abandonedAt = currentApp.abandonedAt ?? null;
          updatedAt = currentApp.updatedAt ?? null;
        } catch {
          // 404 or other error — keep defaults (progress=0)
        }

        const applicationData = transformApplicationData(data, plans);
        console.log('applicationDataSidebar', applicationData);
        const applicationDTO = encodeJSONToBase64(applicationData);
        const isFirstVisit = !hasReachedPhq;

        // Ensure application state (acceptedTier, phqAcceptedAt, etc.) is
        // loaded before computing isPHQEnabled — without this the values
        // default to null and isPHQEnabled is always true on page refresh.
        await useApplicationStore.getState().updateTierFromApplicationState();
        await useApplicationStore.getState().fetchActiveSubscriptionPlans();

        const {
          acceptedTier,
          isMainPlanRejected,
          phqAcceptedAt,
          activeSubscriptionPlans,
        } = useApplicationStore.getState();
        const siteConfig = useGeneralStore.getState().siteConfig;

        const hasActiveMedicalPlan = (() => {
          if (!activeSubscriptionPlans || activeSubscriptionPlans.length === 0)
            return false;
          const activePlanIds = activeSubscriptionPlans.map((p) => p.planId);
          return plans.some(
            (plan) =>
              activePlanIds.includes(plan.id) &&
              plan.category === 'Major Medical'
          );
        })();

        const isPHQEnabled =
          siteConfig?.phqEnabled !== false &&
          !acceptedTier &&
          !isMainPlanRejected &&
          !phqAcceptedAt &&
          !hasActiveMedicalPlan;

        console.log('[Sidebar] isPHQEnabled debug:', {
          isPHQEnabled,
          phqEnabled: siteConfig?.phqEnabled,
          acceptedTier,
          isMainPlanRejected,
          phqAcceptedAt,
          hasActiveMedicalPlan,
          activeSubscriptionPlans: activeSubscriptionPlans?.map((p) => p.planId),
          insurancePlans: plans.map((p) => ({ id: p.id, category: p.category })),
        });

        const response = await AIService.initialize({
          userId: String(user?.id || ''),
          context: {
            page: 'sidebar',
            ...(applicationDTO ? { applicationDTO } : {}),
            name: {
              first_name: user?.firstName || '',
              last_name: user?.lastName || '',
            },
            isFirstVisit,
            isPHQEnabled,
            progress,
            abandonedAt,
            updatedAt,
          },
        });
        if (response?.answer) {
          setLabels({ ...SIDEBAR_LABELS, initial: response.answer });
        }
      } catch (e) {
        // silent fallback to default labels
      } finally {
        setIsLoading(false);
      }
    })();
  }, [user?.firstName, data, plans, user?.id, user?.lastName, hasReachedPhq]);

  // Mark intro animation as shown on first mount
  useEffect(() => {
    if (!sidebarIntroShown) {
      setSidebarIntroShown(true);
      localStorage.setItem('sidebarIntroShown', '1');
    }
  }, [sidebarIntroShown]);

  const prevChatHistoryLengthRef = useRef(chatHistory.length);
  
  useEffect(() => {
    if (pendingMessage && pendingMessageId && pendingMessageTimestamp) {
      const lastChatItem = chatHistory.length > 0 ? chatHistory[chatHistory.length - 1] : null;
      
      // Check if message is already in history (with or without answer)
      const messageInHistory = lastChatItem && lastChatItem.message === pendingMessage;
      
      const lastMessageIsOurs = chatHistory.length > prevChatHistoryLengthRef.current &&
          lastChatItem && 
          lastChatItem.message === pendingMessage && 
          lastChatItem.answer;
      
      const shouldClear = lastMessageIsOurs && !chatLoading;
      
      if (shouldClear) {
        console.log('[Sidebar] Clearing pending message - already in history');
        setPendingMessage(null);
        setPendingMessageId(null);
        setPendingMessageTimestamp(null);
      }
      
      prevChatHistoryLengthRef.current = chatHistory.length;
    } else {
      prevChatHistoryLengthRef.current = chatHistory.length;
    }
  }, [chatHistory, pendingMessage, pendingMessageId, pendingMessageTimestamp, chatLoading]);

  const showInitialMessage = !!labels.initial;
  
  const lastChatItem = chatHistory.length > 0 ? chatHistory[chatHistory.length - 1] : null;
  // Show typing indicator when loading and last message doesn't have an answer yet
  const showTyping = chatLoading && lastChatItem && !lastChatItem.answer;
  
  // Show pending message only if it's not already in history
  const shouldShowPendingMessage = pendingMessage && pendingMessageId && 
    chatLoading && 
    !(chatHistory.length > 0 && 
      chatHistory[chatHistory.length - 1].message === pendingMessage);

  // Scroll to bottom function - used for auto-scroll and during message animation
  const scrollToBottom = useCallback(() => {
    const endRef = isMobile ? mobileMessagesEndRef : messagesEndRef;
    if (endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
    }
  }, [isMobile]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    // Scroll immediately and after a small delay to catch any DOM updates
    scrollToBottom();
    const timeoutId = setTimeout(scrollToBottom, 100);
    
    return () => clearTimeout(timeoutId);
  }, [chatHistory, chatLoading, pendingMessage, showTyping, scrollToBottom]);

  // Lock body scroll on mobile when popup is open
  useEffect(() => {
    // Only lock scroll on mobile when popup is actually open
    if (isMobile && isOpen) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
      
      return () => {
        // Restore scroll position when unlocking
        const bodyTop = document.body.style.top;
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        document.body.style.overflow = '';
        document.body.style.touchAction = '';
        if (bodyTop) {
          window.scrollTo(0, parseInt(bodyTop || '0') * -1);
        }
      };
    } else {
      // Ensure scroll is unlocked when not mobile or when closed
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      document.body.style.overflow = '';
      document.body.style.touchAction = '';
    }
  }, [isMobile, isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleSend = (message: string) => {
    const messageId = `${Date.now()}-${Math.random()}`;
    const timestamp = Date.now();
    setPendingMessage(message);
    setPendingMessageId(messageId);
    setPendingMessageTimestamp(timestamp);
  };

  if (isLoading) {
    return (
      <div>
        <AnimatedDots />
      </div>
    );
  }

  // Mobile popup version
  if (isMobile) {
    return (
      <>
        <ToggleButton open={isOpen} onToggle={handleToggle} />
        {isOpen && (
          <div
            className={cn(
              wrapperPopupClassName,
              'assistant-popup copilotKitPopup'
            )}
            data-open={isOpen ? 'true' : 'false'}
          >
            <div className="copilotKitWindow">
              <SidebarHeader onClose={handleClose} />
              <div className="copilotKitMessagesContainer">
                <div className="copilotKitMessages" ref={mobileMessagesContainerRef}>
                  {showInitialMessage && (
                    <AssistantMessage content={labels.initial} onAnimationUpdate={scrollToBottom} />
                  )}
                  {chatHistory.map((chatItem, idx) => {
                    const isLastItem = idx === chatHistory.length - 1;
                    const isLastUser = isLastItem && !chatItem.answer;
                    const uniqueKey = `${chatItem.session_id}-${idx}-${chatItem.message}-${chatItem.answer?.substring(0, 20) || 'no-answer'}`;
                    return (
                      <React.Fragment key={uniqueKey}>
                        <UserMessage
                          content={chatItem.message}
                          isLastUser={isLastUser}
                          showTyping={false}
                        />
                        {chatItem.answer && (
                          <AssistantMessage content={chatItem.answer} onAnimationUpdate={scrollToBottom} />
                        )}
                      </React.Fragment>
                    );
                  })}
                  {shouldShowPendingMessage && (
                    <UserMessage
                      key={`pending-${pendingMessageId}`}
                      content={pendingMessage}
                      isLastUser={false}
                      showTyping={false}
                    />
                  )}
                  {showTyping && (
                    <AssistantTyping isLoading={true} />
                  )}
                  <div ref={mobileMessagesEndRef} />
                </div>
              </div>
              <SidebarInput onSend={handleSend} inProgress={chatLoading} />
            </div>
          </div>
        )}
      </>
    );
  }

  // Desktop sidebar version
  return (
    <div className={cn('copilotKitSidebarContentWrapper', wrapperSidebarClassName)}>
      <div
        className={cn(
          'assistant-sidebar copilotKitSidebar',
          !currentStep && 'firstscreen'
        )}
      >
        <div className="copilotKitWindow">
          <SidebarHeader />
          <div className="copilotKitMessagesContainer">
            <div className="copilotKitMessages" ref={messagesContainerRef}>
              {showInitialMessage && (
                <AssistantMessage content={labels.initial} onAnimationUpdate={scrollToBottom} />
              )}
              {chatHistory.map((chatItem, idx) => {
                const isLastItem = idx === chatHistory.length - 1;
                const isLastUser = isLastItem && !chatItem.answer;
                const uniqueKey = `${chatItem.session_id}-${idx}-${chatItem.message}-${chatItem.answer?.substring(0, 20) || 'no-answer'}`;
                return (
                  <React.Fragment key={uniqueKey}>
                    <UserMessage
                      content={chatItem.message}
                      isLastUser={isLastUser}
                      showTyping={false}
                    />
                    {chatItem.answer && (
                      <AssistantMessage content={chatItem.answer} onAnimationUpdate={scrollToBottom} />
                    )}
                  </React.Fragment>
                );
              })}
              {shouldShowPendingMessage && (
                <UserMessage
                  key={`pending-${pendingMessageId}`}
                  content={pendingMessage}
                  isLastUser={false}
                  showTyping={false}
                />
              )}
              {showTyping && (
                <AssistantTyping isLoading={true} />
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          <SidebarInput onSend={handleSend} inProgress={chatLoading} />
        </div>
      </div>
    </div>
  );
};
